import { Select as Base } from "@base-ui/react/select";
import type { ComponentProps, ReactNode } from "react";

import "./surface.css";
import "./select.css";
import "./focus.css";
import { cn } from "./cn";

function ChevronIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="m3.5 8.5 3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface SelectTriggerProps extends Omit<ComponentProps<typeof Base.Trigger>, "className"> {
  placeholder?: ReactNode;
  className?: string;
}

function SelectTrigger({ placeholder, className, ...rest }: SelectTriggerProps) {
  return (
    <Base.Trigger className={cn("rc-select-trigger", className)} {...rest}>
      <Base.Value placeholder={placeholder} />
      <Base.Icon className="rc-select-trigger__icon">
        <ChevronIcon />
      </Base.Icon>
    </Base.Trigger>
  );
}

interface SelectContentProps {
  children: ReactNode;
  sideOffset?: number;
  className?: string;
}

function SelectContent({ children, sideOffset = 6, className }: SelectContentProps) {
  return (
    <Base.Portal>
      <Base.Positioner className="rc-select-positioner" sideOffset={sideOffset}>
        <Base.Popup className={cn("rc-surface", "rc-select-popup", className)}>
          <Base.List>{children}</Base.List>
        </Base.Popup>
      </Base.Positioner>
    </Base.Portal>
  );
}

interface SelectItemProps extends Omit<ComponentProps<typeof Base.Item>, "className"> {
  children: ReactNode;
  className?: string;
}

function SelectItem({ children, className, ...rest }: SelectItemProps) {
  return (
    <Base.Item className={cn("rc-select-item", className)} {...rest}>
      <Base.ItemIndicator className="rc-select-item__indicator">
        <CheckIcon />
      </Base.ItemIndicator>
      <Base.ItemText>{children}</Base.ItemText>
    </Base.Item>
  );
}

/**
 * Token-styled Select on Base UI's headless Select. Compound API:
 * `<Select.Root><Select.Trigger placeholder="…" /><Select.Content><Select.Item value="…">…</Select.Item></Select.Content></Select.Root>`.
 * Keyboard select, type-ahead, ARIA and focus restore come from Base UI.
 */
export const Select = {
  Root: Base.Root,
  Trigger: SelectTrigger,
  Content: SelectContent,
  Item: SelectItem,
};
